import React, { useEffect, useState } from "react";
import {
  XMarkIcon,
  BuildingLibraryIcon,
  UserIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import { institutionAPI } from "../../../services/api";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "react-query";

const EditInstitution = ({ isOpen, onClose, institution, onUpdated }) => {
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    registration_no: "",
    name: "",
    institution_type_id: "",
    address: "",
    contact_person: "",
    contact_number: "",
    email: "",
    status: "pending",
    structures: [],
    remarks: "",
  });

  // --- Fetch institution details ---
  const {
    data: institutionDetails,
    isLoading: isLoadingInstitution,
    refetch: refetchInstitution,
  } = useQuery(
    ["institutionDetails", institution?.institution_id],
    async () => {
      if (!institution?.institution_id) return null;
      const res = await institutionAPI.getInstitutionDetails(
        institution.institution_id
      );
      return res.data?.data?.institution_details;
    },
    {
      enabled: false, // we will manually refetch when modal opens
      onError: (error) => {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch institution details";
        toast.error(message);
      },
      onSuccess: (data) => {
        if (data) {
          let statusValue = "pending";
          const institutionStatus = data.status?.toLowerCase();
          if (["active", "inactive", "archived", "pending"].includes(institutionStatus)) {
            statusValue = institutionStatus;
          }

          setFormData({
            registration_no: data.registration_no || "",
            name: data.name || "",
            institution_type_id: data.institution_type?.institution_type_id
              ? String(data.institution_type.institution_type_id)
              : "",
            address: data.address || "",
            contact_person: data.contact_person || "",
            contact_number: data.contact_number || "",
            email: data.email || "",
            status: statusValue,
            structures:
              data.structures?.map((s) => ({
                id: s.structure_id,
                structure_id: s.structure_id,
                position: s.position,
                is_head: Number(s.is_head),
                member_name: s.member_name,
                member_contact: s.member_contact,
                member_email: s.member_email,
                start_date: s.start_date,
                end_date: s.end_date,
              })) || [],
            remarks: data.additionalInfo || "",
          });
        }
      },
    }
  );

  // --- Refetch institution when modal opens ---
  useEffect(() => {
    if (isOpen && institution?.institution_id) {
      refetchInstitution();
    } else if (!isOpen) {
      // reset form on modal close
      setFormData({
        registration_no: "",
        name: "",
        institution_type_id: "",
        address: "",
        contact_person: "",
        contact_number: "",
        email: "",
        status: "pending",
        structures: [],
        remarks: "",
      });
    }
  }, [isOpen, institution?.institution_id]);

  // --- Fetch institution types ---
  const { data: institutionTypes = [] } = useQuery(
    ["institutionTypes"],
    async () => {
      const res = await institutionAPI.getInstitutionTypes({ per_page: 100 });
      return res.data?.data?.institution_types || [];
    },
    {
      onError: (error) => {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch institution types";
        toast.error(message);
      },
      staleTime: 5 * 60 * 1000,
    }
  );

  // --- Update mutation ---
  const updateInstitutionMutation = useMutation(
    async (payload) => {
      return await institutionAPI.updateInstitutions(
        institutionDetails?.institution_id || institution?.institution_id,
        payload
      );
    },
    {
      onSuccess: () => {
        toast.success("Institution updated successfully!");
        queryClient.invalidateQueries(["institutions"]);
        queryClient.invalidateQueries(["institutionDetails"]);

        if (onUpdated) {
          const updatedForTable = {
            ...institutionDetails,
            ...formData,
            type:
              institutionTypes.find(
                (t) => t.id === formData.institution_type_id
              )?.name || "N/A",
            status:
              formData.status.charAt(0).toUpperCase() + formData.status.slice(1),
            status_raw: formData.status,
            additionalInfo: formData.remarks,
          };
          onUpdated(updatedForTable);
        }

        onClose();
      },
      onError: (error) => {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update institution";
        toast.error(message);
      },
    }
  );

  const handleChange = (field, value) => {
    const updatedValue =
      field === "institution_type_id" && value !== "" ? Number(value) : value;
    setFormData((prev) => ({ ...prev, [field]: updatedValue }));
  };

  const handleStructureChange = (index, field, value) => {
    const updated = [...formData.structures];
    updated[index][field] = value;
    setFormData((prev) => ({ ...prev, structures: updated }));
  };

  const handleSubmit = () => {
    const institutionId =
      institutionDetails?.institution_id || institution?.institution_id;
    if (!institutionId) {
      toast.error("Missing institution ID!");
      return;
    }

    const payload = {
      name: formData.name,
      institution_type_id: formData.institution_type_id,
      address: formData.address,
      contact_person: formData.contact_person,
      contact_number: formData.contact_number,
      email: formData.email,
      status: formData.status,
      additionalInfo: formData.remarks,
      structures: formData.structures.map((s) => ({
        id: s.structure_id || s.id,
        position: s.position,
        is_head: Number(s.is_head),
        member_name: s.member_name,
        member_contact: s.member_contact,
        member_email: s.member_email,
        start_date: s.start_date,
        end_date: s.end_date || null,
      })),
    };

    updateInstitutionMutation.mutate(payload);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-6xl overflow-hidden">
        {/* HEADER */}
        <div className="flex justify-between items-center border-b px-6 py-4">
          <h2 className="text-lg font-semibold">Edit Institution Profile</h2>
          <button onClick={onClose}>
            <XMarkIcon className="w-6 h-6 text-gray-600 hover:text-gray-800" />
          </button>
        </div>

        {/* BODY */}
        {isLoadingInstitution ? (
          <div className="p-12 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="p-6 space-y-6 overflow-y-auto max-h-[65vh]">
          {/* Institution Info */}
          <section>
            <h3 className="flex items-center gap-2 text-md font-semibold mb-3">
              <BuildingLibraryIcon className="w-5 h-5 text-blue-600" />
              Institution Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label>Registration Number</label>
                <input
                  type="text"
                  value={formData.registration_no}
                  onChange={(e) =>
                    handleChange("registration_no", e.target.value)
                  }
                  className="border rounded-md px-3 py-2 mt-1"
                />
              </div>

              <div className="flex flex-col">
                <label>Institution Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className="border rounded-md px-3 py-2 mt-1"
                />
              </div>

              <div className="flex flex-col">
                <label>Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                  className="border rounded-md px-3 py-2 mt-1"
                />
              </div>

              <div className="flex flex-col">
                <label>Institution Type</label>
                <select
                  value={formData.institution_type_id}
                  onChange={(e) =>
                    handleChange("institution_type_id", e.target.value)
                  }
                  className="border rounded-md px-3 py-2 mt-1"
                >
                  <option value="">Select Institution Type</option>
                  {institutionTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {/* Contact Info */}
          <section>
            <h3 className="flex items-center gap-2 text-md font-semibold mb-3">
              <PhoneIcon className="w-5 h-5 text-blue-600" />
              Contact Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label>Contact Person</label>
                <input
                  type="text"
                  value={formData.contact_person}
                  onChange={(e) =>
                    handleChange("contact_person", e.target.value)
                  }
                  className="border rounded-md px-3 py-2 mt-1"
                />
              </div>

              <div className="flex flex-col">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className="border rounded-md px-3 py-2 mt-1"
                />
              </div>

              <div className="flex flex-col">
                <label>Phone Number</label>
                <input
                  type="text"
                  value={formData.contact_number}
                  onChange={(e) =>
                    handleChange("contact_number", e.target.value)
                  }
                  className="border rounded-md px-3 py-2 mt-1"
                />
              </div>
            </div>
          </section>

          {/* Structures */}
          {formData.structures.map((s, idx) => (
            <section key={idx}>
              <h3 className="flex items-center gap-2 text-md font-semibold mb-3">
                <UserIcon className="w-5 h-5 text-blue-600" />
                {s.is_head === 1 ? "Head of Institution" : "Member"}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex flex-col">
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={s.member_name}
                    onChange={(e) =>
                      handleStructureChange(idx, "member_name", e.target.value)
                    }
                    className="border rounded-md px-3 py-2 mt-1"
                  />
                </div>

                <div className="flex flex-col">
                  <label>Position</label>
                  <input
                    type="text"
                    value={s.position}
                    onChange={(e) =>
                      handleStructureChange(idx, "position", e.target.value)
                    }
                    className="border rounded-md px-3 py-2 mt-1"
                  />
                </div>

                <div className="flex flex-col">
                  <label>Contact Number</label>
                  <input
                    type="text"
                    value={s.member_contact}
                    onChange={(e) =>
                      handleStructureChange(
                        idx,
                        "member_contact",
                        e.target.value
                      )
                    }
                    className="border rounded-md px-3 py-2 mt-1"
                  />
                </div>

                <div className="flex flex-col">
                  <label>Email</label>
                  <input
                    type="email"
                    value={s.member_email}
                    onChange={(e) =>
                      handleStructureChange(idx, "member_email", e.target.value)
                    }
                    className="border rounded-md px-3 py-2 mt-1"
                  />
                </div>

                <div className="flex flex-col">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={s.start_date || ""}
                    onChange={(e) =>
                      handleStructureChange(idx, "start_date", e.target.value)
                    }
                    className="border rounded-md px-3 py-2 mt-1"
                  />
                </div>

                <div className="flex flex-col">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={s.end_date || ""}
                    onChange={(e) =>
                      handleStructureChange(idx, "end_date", e.target.value)
                    }
                    className="border rounded-md px-3 py-2 mt-1"
                  />
                </div>
              </div>
            </section>
          ))}

          {/* Remarks */}
          <section>
            <h3 className="flex items-center gap-2 text-md font-semibold mb-3">
              <DocumentTextIcon className="w-5 h-5 text-blue-600" />
              Remarks / Justifications
            </h3>
            <textarea
              rows={3}
              value={formData.remarks}
              onChange={(e) => handleChange("remarks", e.target.value)}
              className="border rounded-md px-3 py-2 w-full"
            />
          </section>

          {/* Status */}
          <section>
            <h3 className="flex items-center gap-2 text-md font-semibold mb-3">
              <CheckCircleIcon className="w-5 h-5 text-blue-600" />
              Operational Status
            </h3>
            <div className="w-1/2">
              <select
                value={formData.status}
                onChange={(e) => handleChange("status", e.target.value)}
                className="border rounded-md px-3 py-2 w-full"
              >
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </section>
          </div>
        )}

        {/* FOOTER */}
        <div className="flex justify-end gap-3 border-t px-6 py-4">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={updateInstitutionMutation.isLoading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {updateInstitutionMutation.isLoading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditInstitution;
